import CruciblePiece from './cruciblePiece';
import * as THREE from 'three';
import { ClockConfig } from '../config';
import * as config from '../config.json';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

export class Clock extends CruciblePiece {
  protected _config: ClockConfig;
  protected _fontLoader: THREE.FontLoader;
  protected _hands: { [key: string]: THREE.Object3D };
  protected _clockOnMintDate: {
    [key: string]: number;
  };

  protected _isAnimating: boolean;
  protected _time: number;

  protected _isMintTextVisible: boolean;
  protected _mintTextGroup: THREE.Group;

  constructor() {
    super();
    this._config = config.clock;
    this._fontLoader = new THREE.FontLoader();

    this._hands = {};
    this._mintTextGroup = new THREE.Group();
    this._clockOnMintDate = {};

    this._isMintTextVisible = false;
    this._isAnimating = false;
    this._time = 0;
  }

  setup(gltf: GLTF) {
    // Reset objects when setting up
    this._isMintTextVisible = false;
    this._isAnimating = false;
    this._hands = {};
    this._mintTextGroup = new THREE.Group();
    this._clockOnMintDate = {};

    this.group = gltf.scene;
    this.group.name = 'ClockGroup';
    this.group.scale.set(0.02, 0.02, 0.02); // This should be removed with final models

    this.group.traverse((obj: any) => {
      if (obj.isMesh) {
        obj.castShadow = true;
        obj.receiveShadow = true;
        if (obj.material.map) obj.material.map.anisotropy = 16;
      }
    });

    for (const key in this._config.hands) {
      const hand = this._config.hands[key];
      const handNode = this.group.getObjectByName(hand.node);

      if (handNode) {
        this._hands[key] = handNode;
      }
    }

    // Position Mint Text object in the center of the clock like the hands
    const centerPos = Object.values(this._hands)[0].position;
    this._mintTextGroup.position.set(centerPos.x, 190, centerPos.z);
  }

  setupMintDate(mintTimestamp: number) {
    // Setup hands mintDate position
    const mintDate = new Date(mintTimestamp * 1000); // not in UTC
    const mintSeconds = mintDate.getUTCSeconds();
    const mintMinutes = mintDate.getUTCMinutes();
    const mintHours = mintDate.getUTCHours();
    const mintDay = mintDate.getUTCDate();
    const mintMonth = mintDate.getUTCMonth();

    const totalDaysInMonth = new Date(
      mintDate.getUTCFullYear(),
      mintMonth + 1,
      0
    ).getDate();

    const secondsRotation = (mintSeconds / 60) * (2 * Math.PI);
    const minutesRotation = (mintMinutes / 60) * (2 * Math.PI);
    const hoursRotation = (mintHours / 24) * (2 * Math.PI);
    const daysRotation = (mintDay / totalDaysInMonth) * (2 * Math.PI);
    const monthsRotation = (mintMonth / 12) * (2 * Math.PI);

    this._clockOnMintDate = {
      seconds: secondsRotation,
      minutes: minutesRotation,
      hours: hoursRotation,
      days: daysRotation,
      months: monthsRotation
    };
  }

  update(mintNumber: number, mintTimestamp: number, color: THREE.Color) {
    this.createMintTextGeometry(mintNumber, mintTimestamp, 230, 0.3, color);
  }

  animate(timeElapsed: number) {
    if (this._isAnimating) {
      this.animateHandsToMintDate(1, timeElapsed);
    }

    if (this._isMintTextVisible) {
      this._mintTextGroup.rotation.y += 0.05 * timeElapsed;
    }

    if (!this._isAnimating && !this._isMintTextVisible) {
      for (const key in this._hands) {
        if (this._hands[key]) {
          this._hands[key].rotation.y +=
            this._config.hands[key].speed * timeElapsed;
        }
      }
    }
  }

  playOnClick(raycaster: THREE.Raycaster) {
    const intersects = raycaster.intersectObjects(this.group.children, true);

    if (intersects.length > 0) {
      this.toggleMintText();
    }
  }

  animateHandsToMintDate(duration: number, timeElapsed: number) {
    this._time += timeElapsed;
    const t = this._time / duration;

    if (this._time < duration) {
      // for each hand,
      if (this._hands.seconds) {
        this._hands.seconds.rotation.y = this._lerp(
          this._hands.seconds.rotation.y,
          this._clockOnMintDate.seconds,
          t
        );
      }

      if (this._hands.minutes) {
        this._hands.minutes.rotation.y = this._lerp(
          this._hands.minutes.rotation.y,
          this._clockOnMintDate.minutes,
          t
        );
      }

      if (this._hands.hours) {
        this._hands.hours.rotation.y = this._lerp(
          this._hands.hours.rotation.y,
          this._clockOnMintDate.hours,
          t
        );
      }

      if (this._hands.days) {
        this._hands.days.rotation.y = this._lerp(
          this._hands.days.rotation.y,
          -this._clockOnMintDate.days,
          t
        );
      }

      if (this._hands.months) {
        this._hands.months.rotation.y = this._lerp(
          this._hands.months.rotation.y,
          this._clockOnMintDate.months,
          t
        );
      }
    } else {
      this._isAnimating = false;
    }
  }

  _lerp(start: number, end: number, amount: number) {
    return (1 - amount) * start + amount * end;
  }

  toggleMintText() {
    if (!this._isMintTextVisible) {
      this._isAnimating = true;
      this._time = 0;

      this.group.add(this._mintTextGroup);
      this._isMintTextVisible = true;
    } else {
      this.group.remove(this._mintTextGroup);
      this._isMintTextVisible = false;
    }
  }

  async createMintTextGeometry(
    mintNumber: number,
    mintTimestamp: number,
    radius: number,
    circleRatio: number,
    color: THREE.Color
  ) {
    const font: THREE.Font = await this._fontLoader.loadAsync(
      this._config.fontUrl
    );

    const mintDate = new Date(mintTimestamp * 1000); // not in UTC

    let mintDateText: string = mintDate.toLocaleDateString('UTC');

    if (mintNumber > -1) {
      const paddedMintNumber = mintNumber.toString().padStart(6, '0');
      mintDateText = `${mintDateText} | #${paddedMintNumber}`;
    }

    const characterCount: number = mintDateText.length;
    const angleInterval: number = (2 * Math.PI * circleRatio) / characterCount;

    const material: THREE.Material = new THREE.MeshPhongMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 3
    });

    for (let i = 0; i < characterCount; i++) {
      let textGeometry: THREE.TextGeometry = new THREE.TextGeometry(
        mintDateText[i],
        {
          font: font,
          size: 32,
          height: 1,
          curveSegments: 5,
          bevelEnabled: true,
          bevelThickness: 0.1,
          bevelSize: 0.1,
          bevelSegments: 1
        }
      );

      textGeometry.center();

      let textMesh: THREE.Mesh = new THREE.Mesh(textGeometry, material);
      textMesh.position.set(
        Math.cos(angleInterval * i) * radius,
        10,
        Math.sin(angleInterval * i) * radius
      );

      const position: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
      textMesh.lookAt(position);

      this._mintTextGroup.add(textMesh);
    }
  }
}
